import { useEffect, useRef, useCallback } from 'react';

// 12-hour working session (HIPAA middle-ground): clinic staff shouldn't be
// signed out during a workday. Warn 5 minutes before the 12h mark.
const TIMEOUT_MS = 12 * 60 * 60 * 1000;      // 12 hours
const WARNING_MS = (12 * 60 - 5) * 60 * 1000; // warn at 11h55m

interface Options {
  onWarn: () => void;
  onLogout: () => void;
  enabled?: boolean;
}

export function useInactivityTimeout({ onWarn, onLogout, enabled = true }: Options) {
  const warnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warned = useRef(false);

  const reset = useCallback(() => {
    if (!enabled) return;
    if (warnTimer.current) clearTimeout(warnTimer.current);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    warned.current = false;
    warnTimer.current = setTimeout(() => {
      warned.current = true;
      onWarn();
    }, WARNING_MS);
    logoutTimer.current = setTimeout(onLogout, TIMEOUT_MS);
  }, [enabled, onWarn, onLogout]);

  useEffect(() => {
    if (!enabled) return;
    // Always reset on activity — even if warning is showing, clicking dismisses it
    // and restarts the full 30-minute window.
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    const handler = () => reset();
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    reset();
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (warnTimer.current) clearTimeout(warnTimer.current);
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
    };
  }, [enabled, reset]);

  return { reset };
}
